import { useMemo, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface Guess {
  reg: string;
  km: number;
  dir: string;
}

interface View {
  photo: string;
  guesses: Guess[];
  maxGuesses: number;
  regions: string[];
  answer?: { name: string; region: string };
  credit?: string;
  wikidata?: string;
}

const COPY = {
  unknown_region: "Такої області нема",
  already_guessed: "Вже було",
};

/** "Львівська область" → "Львівська" — the list is long, the suffix is noise. */
function short(name: string): string {
  return name.replace(/\s*область$/, "");
}

export function Dezze({ date }: { date?: string | undefined }) {
  const g = useGame<View>("dezze", COPY, date);
  const [zoom, setZoom] = useState(false);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(() => new Set(state?.view.guesses.map((x) => x.reg) ?? []), [state?.view.guesses]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const left = v.maxGuesses - v.guesses.length;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ДЕ ЦЕ?" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <button
          type="button"
          onClick={() => setZoom(true)}
          className="block w-full overflow-hidden rounded-2xl bg-tgsection"
        >
          <img
            src={v.photo}
            alt="де це?"
            className="h-[46vh] w-full object-cover"
            loading="eager"
            referrerPolicy="no-referrer"
          />
        </button>
        <div className="mt-1.5 text-center text-xs text-tghint">
          {finished ? "фото з Wikimedia Commons" : `яка це область? залишилось спроб: ${left}`}
        </div>

        {v.guesses.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {[...v.guesses].reverse().map((x) => (
              <li
                key={x.reg}
                className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm ${
                  x.km === 0 ? "bg-ok/15" : "bg-tgsection"
                }`}
              >
                <span className="font-medium">{short(x.reg)}</span>
                <span className="text-tghint">{x.km === 0 ? "🎉 в точку" : `${x.km} км ${x.dir}`}</span>
              </li>
            ))}
          </ul>
        )}

        {!finished && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {v.regions.map((r) => (
              <button
                key={r}
                type="button"
                disabled={g.busy || guessed.has(r)}
                onClick={() => void g.run(() => api.guess<View>("dezze", r, date))}
                className="rounded-full bg-[rgba(128,128,128,0.18)] px-3 py-1.5 text-xs active:opacity-70 disabled:opacity-30"
              >
                {short(r)}
              </button>
            ))}
          </div>
        )}
      </div>

      {!finished && (
        <div className="px-4" style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
          <div className="flex">
            <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("dezze", date))} />
          </div>
        </div>
      )}

      {zoom && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/90" onClick={() => setZoom(false)}>
          <img src={v.photo} alt="де це?" className="max-h-full max-w-full object-contain" referrerPolicy="no-referrer" />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Краєзнавець!" : "Не сьогодні"}
          subtitle={won ? `за ${v.guesses.length} · стрік ${s.me.streak} 🔥` : "стрік згорів"}
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.answer && (
            <div className="mt-4 rounded-xl bg-[rgba(128,128,128,0.12)] px-4 py-3 text-center">
              <div className="text-lg font-bold">{v.answer.name}</div>
              <div className="mt-0.5 text-xs text-tghint">{v.answer.region}</div>
              {v.credit && (
                <a href={v.credit} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-tgbutton underline">
                  фото на Commons ↗
                </a>
              )}
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
